<template>
    <div>

        <CostTypesModal @costTypeCreated="onCostTypeCreated" @costTypeUpdated="onCostTypeUpdated" :show-modal-on="['CostTypes', 'ControlPanelInvestmentsCostTypes']" />
        <ItemModal :show-modal-on="['AddInvestmentItem', 'EditInvestmentItem', 'ControlPanelAddInvestmentItem', 'ControlPanelEditInvestmentItem']" @edited="onItemEdited" @created="onItemCreated" @deleted="onItemDeleted" />
               
        <FilterBar
            cookieName="BalanceSheetFilterOptionsVue"
            :mainFilters="['DateRangeFilter', 'BalanceSheetSorting', 'CostTypeFilter']"
            @change="filtersChanged"
            filterBarType="balance_sheet"
            ref="balancesheet-table-filterbar"
        >
            <template v-slot:right-bar>
                <GekkoButton 
                    @click="handleHidePaidItems"
                    :text="getHidePaidItemsText" 
                    :icon="getHidePaidItemsIconClass"
                    class="text-info px-0"
                    variant="link"
                />
            </template>
        </FilterBar>
        
        <div v-if="!loading && items.length" id="table-container" class="table-wrap content--inner">
            <overlay-scrollbars ref="reportsOverlayScroll" :options="scrollBarOptions">
            <b-table
                ref="table"
                :items="items"
                :fields="fields"
                :key="tableKey"
                :filter="filterKey"
                :filter-function="filterTable"
                class="balance-sheet-table"
                :class="getTableYearsClass"
            >
                <template #head()="scope">
                    <div>
                        <div 
                            v-if="scope.label !== 'Remaining' && scope.label !== 'write_off' && scope.label !== 'amount'" 
                            :class="[getHeaderAlignment(scope.column), 'fixed-table-content']"
                        >
                            <span v-if="scope.label === 'cost_type'" class="" v-translate>Type</span>
                            <span v-if="scope.label === 'date'" class="" v-translate>Date</span>
                            <span v-if="scope.label !== 'date' && scope.label !== 'cost_type'">{{scope.label}}</span>
                        </div>
                        
                        <div v-if="scope.label === 'amount'" :class="[getHeaderAlignment(scope.column), 'fixed-table-content text-right']">
                            {{ $gettext('Original Investment') }}
                        </div>

                        <div v-if="scope.label === 'write_off'" :class="[getHeaderAlignment(scope.column), 'fixed-table-content shadow-right']">
                            <div>
                                <span style="white-space: nowrap;">{{ $gettext('Write Off') }}</span> {{ $gettext('Years') }}
                                <font-awesome-icon id="tooltip-writeoff" class="text-info tooltip-icon tooltip-icon--question" icon="fa-solid fa-circle-question" />
                                <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-writeoff" placement="bottom" triggers="hover" container="#table-container">
                                    <div class="tooltip-title">
                                        {{ $gettext('Write Off Years') }}
                                    </div>
                                    <div class="tooltip-text">
                                        {{ $gettext('In this column you can indicate how much years you want to write off your investment. It will automatically calculate the write off amounts over the different years.') }}
                                    </div>
                                </b-tooltip>
                            </div>
                        </div>
                        
                        <div v-if="scope.label === 'Remaining'" :class="[getHeaderAlignment(scope.column), 'fixed-table-content shadow-left']">
                            <div>{{ $gettext('Balance Sheet') }} {{ getCurrentYear }}</div>
                        </div>
                    </div>
                </template>
                <template #cell(date)="row">
                    <div class="fixed-table-content date-col">
                        <span>{{row.item.date | formatDate}}</span>
                    </div>
                </template>
                <template #cell(cost_type)="row">
                    <div class="fixed-table-content costtype-col">
                        <div v-if="row.item.cost_type" class="costtype-icon">
                            <span :id="'costtype-icon' + row.item.id" v-html="get_icon(row.item.cost_type)" />
                            <b-tooltip :target="'costtype-icon' + row.item.id" placement="top" triggers="hover" container="#table-container">
                                <div class="tooltip-text">
                                    {{ getCostTypeText(row.item.cost_type) }}
                                </div>
                            </b-tooltip>
                        </div>
                        <span v-else>-</span>
                    </div>
                </template>
                <template #cell(description)="row">
                    <div class="fixed-table-content description-col">
                        <div>
                            {{ row.item.description }}
                        </div>
                    </div>
                </template>
                <template #cell(amount)="row">
                    <div class="fixed-table-content">
                        <span class="">€&nbsp;{{getAmount(row.item) | formatNumber}}</span>
                    </div>
                </template>
                <template #cell(writeoffyears_count)="row">
                    <div @click="enableEditingRow(row, $event)" class="fixed-table-content write-off-controls">
                        <div class="controls-wrap">
                            <svg @click="subtractWriteoffYear(row.item)" class="write-off-controls--add" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div class="years-count">{{ getWriteoffyearsText(row.item) }}</div>
                            <svg @click="addWriteoffYear(row.item)" xmlns="http://www.w3.org/2000/svg" class="write-off-controls--substract" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                    </div>
                </template>

                <template #cell()="data" >
                    <div v-show="editedRowId === data.item.id && isYearInRange(data)" class="writeoff-value-container">
                        <b-form-input
                            :id="'writeoff_' + data.field.key + '_' + data.item.id"
                            v-model="data.value" 
                            @input="changeWriteoffyearValue(data); setEditedRow(data.item)" 
                            
                            class="form-input"
                            :class="writeoffBorderClass(data.item)" 
                        />
                    </div>
                    <div v-show="editedRowId === data.item.id && isYearDisabled(data)" class="writeoff-value-container disabled-writeoff" />
                    <div 
                        v-if="editedRowId !== data.item.id" @click="enableEditingRow(data, $event)"
                        class="writeoff-value-container"
                        :class="{ 'disabled-writeoff': isYearDisabled(data) }"
                        style="cursor: pointer"
                    >
                        <div v-if="data.value && data.value != 0">€&nbsp;{{data.value | formatNumber}}</div>
                        <div v-else class="writeoff-empty">€&nbsp;0.00</div>
                    </div>
                </template>

                <template #cell(remaining)="row">
                    <div class="inner-border-left shadow-left fixed-table-content">
                        € {{ getRemaining(row.item) | formatNumber }}
                    </div>
                </template>
                <template #cell(actions)="row">
                    <div class="fixed-table-content actions">
                        <div class="action action--warning" v-if="!isBalanceSheet(row.item) && row.item.id !== editedRowId">
                            <font-awesome-icon :id="'action-icon-warning' + row.item.id" icon="fa-solid fa-triangle-exclamation text-warning" />
                            <b-tooltip :custom-class="'tooltip-padded'" :target="'action-icon-warning' + row.item.id" boundary="window" placement="top" triggers="hover" container="#table-container">
                                <div class="tooltip-title">
                                    {{ $gettext('Cost type updated') }}
                                </div>
                                <div class="tooltip-text">
                                    {{ $gettext('This cost is not classified as balance sheet anymore. You can now remove it from the list.') }}
                                </div>
                            </b-tooltip>
                        </div>
                        <div class="action action--delete" v-if="!isBalanceSheet(row.item) && row.item.id !== editedRowId">
                            <GekkoButton 
                                icon="fa-solid fa-trash-can"
                                class="text-danger p-0"
                                variant="link"
                                v-gk-confirm="{
                                    title: $gettext('Delete item'),
                                    message: $gettext('Are you sure you want to delete this cost from the investment table?'),
                                    options: {
                                        variant: 'danger',
                                        confirmText: $gettext('Delete')
                                    },
                                    callback: deleteProfitItem,
                                    callbackParams: row.item,
                                }"
                            />
                        </div>
                        <div class="action action--save">
                            <GekkoButton 
                                @click="(row.item.id === editedRowId) ? saveRow(row.item, $event) : false"
                                icon="fa-solid fa-save"
                                class="text-info p-0"
                                variant="link"
                                :id="'action-icon-save' + row.item.id"
                                :disabled="row.item.id !== editedRowId"
                            />
                            <b-tooltip :target="'action-icon-save' + row.item.id" boundary="window" :delay="{ show: 1000, hide: 50 }" placement="top" triggers="hover" container="#table-container">
                                <div class="tooltip-text">
                                    {{ $gettext('Save') }}
                                </div>
                            </b-tooltip>
                        </div>
                        <div class="action action--undo">
                            <GekkoButton 
                                @click="(row.item.id === editedRowId) ? unsetEditedRow() : false"
                                icon="fa-solid fa-rotate-left"
                                :id="'action-icon-undo' + row.item.id"
                                class="text-info p-0"
                                variant="link"
                                :disabled="row.item.id !== editedRowId"
                            />
                            <b-tooltip :target="'action-icon-undo' + row.item.id" boundary="window" :delay="{ show: 1000, hide: 50 }" placement="top" triggers="hover" container="#table-container">
                                <div class="tooltip-text">
                                    {{ $gettext('Undo changes') }}
                                </div>
                            </b-tooltip>
                        </div>
                        <div class="action action--edit">
                            <GekkoButton 
                                @click="onRowClicked(row.item)"
                                icon="fa-solid fa-pen-to-square"
                                :id="'action-icon-edit' + row.item.id"
                                class="text-info p-0"
                                variant="link"
                            />
                            <b-tooltip :target="'action-icon-edit' + row.item.id" boundary="window" :delay="{ show: 1000, hide: 50 }" placement="top" triggers="hover" container="#table-container">
                                <div class="tooltip-text">
                                    {{ $gettext('Edit this item') }}
                                </div>
                            </b-tooltip>
                        </div>
                    </div>
                </template>
            </b-table>
            </overlay-scrollbars>
            <GekkoButton 
                v-if="nextUrl"
                id="load_more_button"
                :text="$gettext('Load more items')" 
                icon="fa-solid fa-plus"
                class="text-info"
                variant="link"
            />
        </div>
        
        <div v-if="loading" class="spinner">
            <b-spinner variant="primary" />
        </div>

        <div v-if="!loading && !items.length">
            <EmptyState>
                <template v-slot:image>
                    <img src="/static/img/empty-states/empty-state-cost.png">
                </template>
                <template v-slot:title>
                    {{ $gettext('We couldn\'t find any item on your balance sheet') }}
                </template>
                <template v-slot:content>
                    {{ $gettext('Start by creating a new item or setting a cost type as a balance sheet type. All costs with balance sheet cost types will be loaded into the write-off table.') }}
                </template>
                <template v-slot:cta>
                    <GekkoButton 
                        @click="addNewItem()"
                        :text="$gettext('Add item')" 
                        icon="fa-solid fa-plus"
                    />
                    <GekkoButton 
                        :text="$gettext('Manage Cost Types')" 
                        icon="fa-solid fa-arrow-up-right-from-square"
                        :to="{ name: !isControlPanel ? 'CostTypes' : 'ControlPanelInvestmentsCostTypes' }"
                    />
                </template>
            </EmptyState>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import BalanceSheetService from "@/store/services/balancesheet.service";
import CostTypeService from "@/store/services/costtype.service";
import CostTypesModal from "@/components/balance_sheet/CostTypesModal";
import GekkoButton from "@/components/GekkoButton";
import FilterBar from "@/components/list_filters/FilterBar";
import ItemModal from "@/components/balance_sheet/ItemModal";
import EmptyState from "@/components/ui/EmptyState";

export default {
    name: 'BalanceSheetTable',
    components: { CostTypesModal, GekkoButton, FilterBar, ItemModal, EmptyState },
    data () {
        return {
            items: [],
            fields: [
                // {key: "edit_button", label: ""},
                { key: "date", label: "date", thClass: "fix-width" },
                { key: "cost_type", label: "cost_type", tdClass: "text-center", thClass: "fix-width" },
                { key: "description", label: this.$gettext("Description"), thClass: "fix-width" },
                { key: "amount", label: "amount", thClass: "fix-width" },
                { key: "writeoffyears_count", label: "write_off", tdClass: "inner-border-right shadow-right", thClass: "fix-width" },
                { key: "remaining", thClass: "fix-width" },
                { key: "actions", label: this.$gettext("Actions"), tdClass: "text-right", thClass: "fix-width" }
            ],
            costTypeOptions: [],
            tableKey: 0,
            firstYearPos: 5, // constant which sets the position in the fields array where the writeoffyears start
            lastYearPos: -2, // same for the end
            minArrayLength: 7, // (firstYearPos + minArrayLength) min length of the fields array
            filters: {},
            filterKey: "0",
            loading: true,
            firstLoad: true,
            editedRowId: null,
            rowBeforeEdit: null,
            nextUrl: '',
            fetchingItems: false,
            hidePaidItems: false,
            showWriteoffDangerId: null,
            didScroll: false,
            maxWriteoffYears: 10,
            windowSize: window.innerWidth,
            scrollBarOptions: { 
                scrollbars: { 
                    visibility       : "auto",
                    autoHide         : "never",
                    autoHideDelay    : 800,
                    dragScrolling    : true,
                    clickScrolling   : true,
                    touchSupport     : true,
                    snapHandle       : true
                }, 
                overflowBehavior: { y: 'scroll', x: 'scroll' },
            }
        }
    },
    computed: {
        ...mapGetters ({
            currentCompany: "main/getCurrentCompany",
            currentUser: "main/getCurrentUser",
            isControlPanel: "main/isControlPanel",
        }),
        getHidePaidItemsIconClass() {
            if (this.hidePaidItems)
                return "fa-regular fa-eye";
            else
                return "fa-regular fa-eye-slash";
        },
        getCurrentYear() {
            let today = new Date();
            let currentYear = today.getFullYear();
            return currentYear;
        },
        getHidePaidItemsText() {
            if (this.hidePaidItems)
                return this.$gettext('Show written off investments');
            else
                return this.$gettext('Hide written off investments');
        },
        getTableYearsClass () {
            let numberOfYears = 0
            this.fields.forEach(field => {
                if (parseInt(field.key))
                    numberOfYears++
            });

            let stretchThreshold = 4
            if (this.windowSize > 900 && this.windowSize <= 1100) stretchThreshold = 1
            if (this.windowSize > 1100 && this.windowSize < 1200) stretchThreshold = 2
            return 'table-' + ((numberOfYears < stretchThreshold) ? 'stretch' : 'fix')
        }
    },
    watch: {
        loading () {
            if (this.firstLoad) {
                this.firstLoad = false
                this.attachDraggableListeners()
            }
        }
    },
    created() {
        window.addEventListener('resize', this.handleResize);
        window.addEventListener('scroll', this.handleScroll);

        CostTypeService.list({company: this.currentCompany.id}).then((response) => {
            response.data.forEach(costType => {
                this.costTypeOptions.push({
                    text: costType.name,
                    value: costType.id,
                    icon: costType.icon,
                    is_balance_sheet: costType.is_balance_sheet
                });
            });
        });

        this.filters.ordering = "-date"; // initialize ordering

        window.addEventListener('keypress', this.saveRowOnKeyPress);
    },
    mounted () {

    },
    destroyed() {
        window.removeEventListener('keypress', this.saveRowOnKeyPress);
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        addNewItem () {
            var isPremium = this.currentCompany.is_premium
            if (isPremium || this.currentUser.is_accountant)
                this.$router.push({ name: !this.isControlPanel ? 'AddInvestmentItem' : 'ControlPanelAddInvestmentItem' });
            else
                this.$store.commit('togglePremiumModal', 'web_reporting_investments');
        },
        handleResize () {
            this.windowSize = window.innerWidth
        },
        attachDraggableListeners () {
            this.$nextTick(() => {
                const slider = document.querySelector('.balance-sheet-table')
                let isDown = false
                let startX
                let scrollLeft
                if (slider) {
                    slider.addEventListener('mousedown', (e) => {
                        this.didScroll = false
                        isDown = true
                        startX = e.pageX - slider.offsetLeft
                        scrollLeft = this.$refs.reportsOverlayScroll.osInstance().scroll().position.x
                    })
                    slider.addEventListener('mouseleave', () => {
                        isDown = false
                    })
                    slider.addEventListener('mouseup', () => {
                        isDown = false
                    })
                    slider.addEventListener('mousemove', (e) => {
                        if(!isDown) return
                        this.didScroll = true
                        e.preventDefault()
                        const x = e.pageX - slider.offsetLeft
                        const walk = (x - startX) //scroll-fast
                        this.$refs.reportsOverlayScroll.osInstance().scroll({ x: scrollLeft - walk }, 0, 'linear');
                    });
                }
            });
        },
        getBalanceSheetItems(filters) {
            BalanceSheetService.list(filters).then((response) => {
                this.items = response.data.results;
                this.nextUrl = response.data.next;

                let lastYear = null;
                this.items.forEach(item => {
                    item.writeoffyears_count = item.writeoffyear_set.length;

                    item.writeoffyear_set.forEach(writeoffyear => {
                        this.insertYearField(writeoffyear.year.toString());

                        if (lastYear) {
                            if (lastYear < writeoffyear.year) {
                                lastYear = writeoffyear.year;
                            }
                        } else
                            lastYear = writeoffyear.year;
                        
                        item[writeoffyear.year.toString()] = writeoffyear.amount;
                    });

                    item.hide_writeoffyears_count = true;
                });

                if (lastYear) {
                    lastYear++;
                    this.insertYearField(lastYear.toString());
                }

                this.addNecessaryFields();
                this.setNullValues();
                this.removeUnusedFields();
                this.loading = false;
            });
        },
        saveRow(row, event) {
            if (event)
                event.stopPropagation();

            let data = BalanceSheetService.prepareDataForUpdate(row);
            BalanceSheetService.update(row.id, data).then((response) => {
                for (var i = 0; i < this.items.length; i++) {
                    if (this.items[i].id === response.data.id) {

                        const updatedItem = response.data
                        updatedItem.writeoffyears_count = updatedItem.writeoffyear_set.length

                        updatedItem.writeoffyear_set.forEach(writeoffyear => {
                            this.insertYearField(writeoffyear.year.toString())
                            updatedItem[writeoffyear.year.toString()] = writeoffyear.amount
                        });

                        let lastYear = Math.max(...updatedItem.writeoffyear_set.map(writeoffyear => writeoffyear.year))
                        lastYear++;

                        updatedItem[lastYear.toString()] = null
                        updatedItem.hide_writeoffyears_count = true

                        this.$set(this.items, i, updatedItem)

                        this.removeUnusedFields();
                        break;
                    }
                }

                this.$store.commit("setAlert", {
                    title: this.$gettext("Success!"),
                    text: this.$gettext("Investment item saved."),
                    variant: "success",
                    duration: 4
                });
                if (window.mixpanel) {
                    window.mixpanel.track("Balance Sheet: item edited");
                }

                this.editedRowId = null;
            }).catch((response) => {
                let msg = this.$gettext("Error updating the investment item.");
                if (response["non_field_errors"])
                    msg = response["non_field_errors"];
                if (response["writeoffyear_set"]) {
                    response["writeoffyear_set"].forEach(obj => {
                        if (obj["amount"]) {
                            msg = obj["amount"];
                        }
                    });
                }
                this.$store.commit("setAlert", {
                    title: this.$gettext("Something went wrong"),
                    text: msg,
                    variant: "danger",
                    duration: 4
                });
            });
        },
        saveRowOnKeyPress(e) {
            if (e.key === 'Enter') {
                if (this.editedRowId) {
                    let row = this.items.find(item => item.id === this.editedRowId);
                    this.saveRow(row, null);
                }
            }
        },
        onItemDeleted(row) {
            if (!row.cost) {
                BalanceSheetService.delete(row.id).then(() => {
                    this.items = this.items.filter(item => item.id != row.id);

                    this.removeUnusedFields();
                    this.editedRowId = null;

                    this.$store.commit("setAlert", {
                        title: this.$gettext("Item deleted"),
                        text: this.$gettext("The item was removed form the balance sheet."),
                        variant: "success",
                        duration: 4
                    });
                    if (window.mixpanel) {
                        window.mixpanel.track("Balance Sheet: item deleted");
                    }
                }).catch(() => {
                    this.$store.commit("setAlert", {
                        title: this.$gettext("Something went wrong"),
                        text: this.$gettext("The item could not be deleted from the list."),
                        variant: "danger",
                        duration: 4
                    });
                });
            }
        },
        deleteProfitItem(row) {
            BalanceSheetService.delete(row.id).then(() => {
                this.items = this.items.filter(item => item.id != row.id);

                this.removeUnusedFields();
                this.editedRowId = null;

                this.$store.commit("setAlert", {
                    title: this.$gettext("Item deleted"),
                    text: this.$gettext("The profit item was deleted."),
                    variant: "success",
                    duration: 4
                });
            }).catch(() => {
                this.$store.commit("setAlert", {
                    title: this.$gettext("Something went wrong"),
                    text: this.$gettext("The item could not be deleted from the list."),
                    variant: "danger",
                    duration: 4
                });
            });
        },
        changeEditedRow(newRow) {
            if (this.editedRowId !== newRow.id) {
                for (let i = 0; i < this.items.length; i++) {
                    if (this.items[i].id === this.editedRowId) {
                        this.$set(this.items, i, this.rowBeforeEdit)
                        break;
                    }
                }
                this.rowBeforeEdit = JSON.parse(JSON.stringify(newRow));
                this.editedRowId = newRow.id;
            }
        },
        subtractWriteoffYear(row) {
            var isPremium = this.currentCompany.is_premium
            if (isPremium || this.currentUser.is_accountant) {
                this.changeEditedRow(row);

                for (var i = 0; i < this.items.length; i++) {
                    if (this.items[i].id === row.id) {
                        if (this.items[i].writeoffyears_count > 0) {
                            this.items[i].writeoffyears_count--;
                            this.calculateWriteOffYear(this.items[i]);
                        }
                    }
                }
            } else {
                this.$store.commit('togglePremiumModal', 'web_reporting_investments');
            }
        },
        addWriteoffYear(row) {
            var isPremium = this.currentCompany.is_premium
            if (isPremium || this.currentUser.is_accountant) {
                this.changeEditedRow(row);
                for (var i = 0; i < this.items.length; i++) {
                    if (this.items[i].id === row.id) {
                        if (row.writeoffyears_count < this.maxWriteoffYears) {
                            this.items[i].writeoffyears_count++;
                            this.calculateWriteOffYear(this.items[i]);
                        } else {
                            this.$store.commit("setAlert", {
                                title: this.$gettext("Write-Off limit"),
                                text: this.$gettext('Maximum of years in which you can Write-Off is') + ' ' + this.maxWriteoffYears + '.',
                                variant: "danger",
                                duration: 4
                            });
                        }
                    }
                }
            } else {
                this.$store.commit('togglePremiumModal', 'web_reporting_investments');
            }
        },
        calculateWriteOffYear(row) {
            let date = Date.parse(row.date);
            let year = date.getFullYear();
            let item = this.items.find(item => item.id === row.id);
            item.hide_writeoffyears_count = false;

            let sumPreviousYears = 0;
            if (row.writeoffyears_count !== 0) {
                // Add items and fields
                for (let i = 0; i < row.writeoffyears_count - 1; i++) {
                    this.insertYearField(year.toString());

                    let yearAmount = Math.floor((row.amount / row.writeoffyears_count) * 100) / 100;
                    
                    item[year.toString()] = yearAmount;
                    sumPreviousYears = sumPreviousYears + yearAmount;
                    
                    year++;
                }

                // the last year is calculated from substracting the sum of the previous years to the total amount
                this.insertYearField(year.toString());
                item[year.toString()] = this.roundValue(item.amount - sumPreviousYears);
                this.scrollToElement(year, row.id);

                // insert empty field
                year++;
                if (item.writeoffyears_count < this.maxWriteoffYears)
                    this.insertYearField(year.toString());
                
                // Remove items and fields
                let maxWriteoffyearNumber = 0;
                this.fields.forEach(field => {
                    if (parseFloat(field.key))  // is a writeoff year
                        maxWriteoffyearNumber++;
                });

                for (let i = row.writeoffyears_count; i < maxWriteoffyearNumber; i++) {
                    item[year.toString()] = null;
                    year++;
                }

                this.removeUnusedFields();
            } else {
                let today = new Date();
                let currentYear = today.getFullYear();
                for (var i = year; i <= currentYear; i++) {
                    item[i.toString()] = null;
                    item.writeoffyears_count = 0;
                    this.insertYearField(i.toString());
                }
            }
        },
        insertYearField(yearStr) { // checks whether there should be a new field added, and it does if so
            let key_list = this.fields.map(field => field.key);
            if (!key_list.includes(yearStr)) {
                let firstYearKey = parseInt(key_list[this.firstYearPos]);
                let lastYearKey = parseInt(key_list[key_list.length + this.lastYearPos - 1]);
                let newKey = parseInt(yearStr);

                if (newKey < firstYearKey) {
                    this.fields.splice(this.firstYearPos, 0, {
                        key: yearStr, 
                        label: yearStr
                    });
                } else if (newKey > lastYearKey) {
                    this.fields.splice(this.lastYearPos, 0, {
                        key: yearStr, 
                        label: yearStr
                    });
                } else {
                    if (key_list.length > this.minArrayLength) {
                        for (var i = this.firstYearPos; i < key_list.length; i++) {
                            let currentKey = parseInt(key_list[i]);
                            let nextKey = parseInt(key_list[i+1]); // nextKey should never become NaN
                            
                            if (newKey > currentKey && newKey < nextKey) {
                                this.fields.splice(i + 1, 0, {
                                    key: yearStr, 
                                    label: yearStr
                                });
                                break;
                            }
                        }
                    } else {
                        this.fields.splice(this.lastYearPos, 0, {
                            key: yearStr, 
                            label: yearStr
                        });
                    }
                }
            }
        },
        scrollToElement(fieldKey, id = null) {
            this.$nextTick(() => {
                let numberOfYears = 0;
                let fieldPosition = null;

                this.fields.forEach(field => {
                    if (parseInt(field.key))
                        numberOfYears++;
                    if (field.key === fieldKey.toString())
                        fieldPosition = numberOfYears;
                });

                const container = window.document.getElementById("table-container");
                const targetEl = window.document.getElementById('writeoff_' + fieldKey + '_' + id)

                if (container && targetEl) {
                    const unitSize = targetEl.parentElement.offsetWidth
                    let leftScrollTarget = 0;

                    if (fieldPosition > 0)
                        leftScrollTarget = unitSize * fieldPosition - unitSize;

                    this.$refs.reportsOverlayScroll.osInstance().scroll({ x: leftScrollTarget }, 200, 'linear');
                }
            })
        },
        changeWriteoffyearValue(data) {  // sets user input value to the form inputs and validates data
            let item = this.items.find(item => item.id === data.item.id);
            
            item[data.field.key] = data.value;
            
            let year = parseInt(data.field.key);

            let totalWriteoffAmount = 0;
            let investmentDate = Date.parse(item["date"]);
            let investmentYear = investmentDate.getFullYear();

            let today = new Date();
            let currentYear = today.getFullYear();
            let lastYear = investmentYear + item["writeoffyears_count"];
            if (lastYear < currentYear)
                lastYear = currentYear;

            for (var i = investmentYear; i <= lastYear; i++) {
                if (parseFloat(item[i.toString()]))
                    totalWriteoffAmount = totalWriteoffAmount + parseFloat(item[i.toString()]);
            }
            
            if (totalWriteoffAmount > parseFloat(data.item.amount)) 
                this.showWriteoffDangerId = data.item.id;
            else
                this.showWriteoffDangerId = null;

            year++;
            
            if (this.getNWriteoffYears(data.item) < this.maxWriteoffYears) {
                this.insertYearField(year.toString());
            }

            while (item[year.toString()]) {
                item[year.toString()] = null;
                year++;
            }
            item[year.toString()] = null;

            this.removeUnusedFields();
            item.writeoffyears_count = this.getNWriteoffYears(data.item);
            item.hide_writeoffyears_count = true;

            /**
             * In this situation this trick is needed:
             * If the key is not incremented the remaining values not always update
             * If the key is just incremented once, the table gets scrolled to the right
             */
            this.tableKey++;
            this.tableKey--;
        },
        getWriteoffyearsText(item) {
            // return (item.hide_writeoffyears_count) ? '-' : item.writeoffyears_count
            return item.writeoffyears_count
        },
        fieldIsEmpty(fieldKey) {
            let k = true;
            this.items.forEach(item => {
                if (item[fieldKey]) {
                    if (item[fieldKey])
                        k = false;
                }
            });
            return k;
        },
        getMaxYearFromFields() {
            let maxYear = null;

            this.fields.forEach(field => {
                let year = parseInt(field.key);
                if (year) {
                    if (maxYear) {
                        if (year > maxYear && !this.fieldIsEmpty(year.toString()))
                            maxYear = year;
                    } else {
                        if (!this.fieldIsEmpty(year.toString()))
                            maxYear = year;
                    }
                }
            });

            return maxYear;
        },
        getMinYearFromFields() {
            let minYear = null;

            this.fields.forEach(field => {
                let year = parseInt(field.key);
                if (year) {
                    if (minYear) {
                        if (year < minYear && !this.fieldIsEmpty(year.toString()))
                            minYear = year;
                    } else {
                        if (!this.fieldIsEmpty(year.toString()))
                            minYear = year;
                    }
                }
            });

            return minYear;
        },
        getMinInvestmentYear() {
            let today = new Date();
            let minInvestmentYear = today.getFullYear();
            this.items.forEach(item => {
                let investmentDate = Date.parse(item.date);
                let investmentYear = investmentDate.getFullYear();
                if (investmentYear < minInvestmentYear)
                    minInvestmentYear = investmentYear;
            });
            return minInvestmentYear;
        },
        getMaxInvestmentYear() {
            let today = new Date();
            let maxInvestmentYear = today.getFullYear();
            this.items.forEach(item => {
                let investmentDate = Date.parse(item.date);
                let investmentYear = investmentDate.getFullYear();
                if (investmentYear > maxInvestmentYear)
                    maxInvestmentYear = investmentYear;
            });
            return maxInvestmentYear;
        },
        removeUnusedFields() {
            let today = new Date();
            let currentYear = today.getFullYear();

            this.fields.forEach(field => {
                let year = parseInt(field.key);

                if (year) { // is a writeoff year
                    if (
                        (this.getMaxYearFromFields() + 1 < year || this.getMinYearFromFields() > year)
                        && (year < this.getMinInvestmentYear() || year > this.getMaxInvestmentYear())
                        && year !== currentYear
                    )
                        this.fields = this.fields.filter(f => f.key !== year.toString());
                }
            });
        },
        addNecessaryFields() {
            for (var i = this.getMinInvestmentYear(); i <= this.getMaxInvestmentYear(); i++)
                this.insertYearField(i.toString());
        },
        getNWriteoffYears(row) {
            let k = 0;

            this.fields.forEach(field => {
                let year = parseInt(field.key);
                if (year) {
                    if (row[year.toString()]) {
                        k++;
                        year++;
                    }
                }
            });
            
            return k;
        },
        setNullValues() {
            for (var i = 0; i < this.items.length; i++) {
                let date = Date.parse(this.items[i].date);
                let year = date.getFullYear();
                for (var j = year; j <= this.getMaxYearFromFields() + 1; j++) {
                    if (!this.items[i][j]) {
                        this.items[i][j] = null;
                    }
                }
            }
        },
        getCostTypeText(costTypeId) {
            let costType = this.costTypeOptions.find(costType => costType.value === costTypeId);
            if (costType)
                return costType.text;
        },
        getAmount(row) {
            let roundedValue = this.roundValue(row.amount);
            if (!roundedValue)
                return 0;
            return roundedValue;
        },
        getRemaining(row) {
            let today = new Date();
            let currentYear = today.getFullYear();

            let date = Date.parse(row.date);
            let investmentYear = date.getFullYear();

            let paidValue = 0;
            
            for (let i = currentYear; i >= investmentYear; i--) {
                if (row[i.toString()]) {
                    if (parseFloat(row[i.toString()]))
                        paidValue = paidValue + parseFloat(row[i.toString()]);
                }
            }
            
            let bsValue = row.amount - paidValue;
            let roundedValue = this.roundValue(bsValue);
            
            if (!roundedValue)
                return 0;
            
            return roundedValue;
        },
        filtersChanged(filters) {
            this.loading = true;
            if (filters.balance_sheet_ordering)
                filters.ordering = filters.balance_sheet_ordering;
            else
                filters.ordering = "-date";
            filters.company_profile = this.currentCompany.id;
            this.filters = filters;
            this.getBalanceSheetItems(filters);
        },
        filterTable(row) {
            if (row.hided)
                return false;
            else
                return true;
        },
        enableEditingRow(data, event) {
            if(this.didScroll) return

            event.stopPropagation();

            this.changeEditedRow(data.item);
            this.removeUnusedFields();
            
            let inputId = "";
            if (!data.item[data.field.key]) {
                let investmentDate = Date.parse(data.item["date"]);
                let investmentYear = investmentDate.getFullYear();
                let lastYear = investmentYear + data.item["writeoffyears_count"];
                this.scrollToElement(lastYear.toString(), data.item.id);
                inputId = 'writeoff_' + lastYear + '_' + data.item.id;
            } else {
                inputId = 'writeoff_' + data.field.key + '_' + data.item.id;
            }

            if (window.document.getElementById(inputId)) {
                window.document.getElementById(inputId).focus();
                setTimeout(function () {
                    window.document.getElementById(inputId).select();
                }, 100);
            }
        },
        onCostTypeCreated(val) {
            this.costTypeOptions.push({text: val.name, value: val.id});
        },
        onCostTypeUpdated(val) {
            for (var i = 0; i < this.costTypeOptions.length; i++) {
                if (this.costTypeOptions[i].value === val.id) {
                    const updatedCosttype = {
                        text: val.name,
                        value: val.id,
                        icon: val.icon,
                        is_balance_sheet: val.is_balance_sheet
                    };
                    this.$set(this.costTypeOptions, i, updatedCosttype)
                    break;
                }
            }
        },
        // onCostEdit(cost) {
        //     let item = this.items.find(item => item.cost === cost.id);

        //     // writeoff years are set to zero
        //     for (var i = 0; i < this.fields.length; i++) {
        //         if (parseInt(item[this.fields[i].key])) {
        //             item[this.fields[i].key] = null;
        //         }
        //     }
        //     item.writeoffyear_set = [];
        //     item.writeoffyears_count = 0;
        //     item.hide_writeoffyears_count = true;
            
        //     item.date = cost.date;
        //     item.cost_type = cost.cost_type;
        //     item.description = cost.description;
        //     item.amount = cost.amount;

        //     this.addNecessaryFields();
        //     this.removeUnusedFields();
        // },
        onItemEdited(newItem) {
            for (var i = 0; i < this.items.length; i++) {
                if (this.items[i].id === newItem.id) {
                    newItem.hide_writeoffyears_count = true;
                    this.$set(this.items, i, newItem)
                    break;
                }
            }
            this.removeUnusedFields();
        },
        onItemCreated(newItem) {
            newItem.hide_writeoffyears_count = true;
            this.items.unshift(newItem);
        },
        get_icon(costTypeId) {
            if (costTypeId) {
                let costType = this.costTypeOptions.find(item => item.value === costTypeId);
                if (costType) {
                    let icon = costType.icon;
                    return "&#x" + icon + ";";
                }
            }
            return '';
        },
        roundValue(value) {
            if (value) {
                let floatValue = parseFloat(value);
                if (floatValue)
                    return parseFloat(floatValue.toFixed(2));
            }
        },
        getHeaderAlignment(headerId) {
            if (headerId === "date" || headerId === "description") {
                return "text-left";
            } else if (headerId === "cost_type") {
                return "text-center";
            } else if (headerId === "actions") {
                return "text-right";
            } else if (headerId === "writeoffyears_count" || headerId === "remaining" || headerId === "amount") {
                return "text-right";
            } else {
                return "writeoff-header";
            }
        },
        onRowClicked(row) {
            if (row.cost) {
                this.$router.push({ name: !this.isControlPanel ? 'BalanceSheetEditCost' : 'ControlPanelBalanceSheetEditCost', params: {costId: row.cost}});
            } else {
                this.$router.push({ name: !this.isControlPanel ? 'EditInvestmentItem' : 'ControlPanelEditInvestmentItem', params: {investmentId: row.id}});
            }
        },
        setEditedRow(row) {
            this.editedRowId = row.id;
        },
        unsetEditedRow() {
            for (var i = 0; i < this.items.length; i++) {
                if (this.items[i].id === this.editedRowId) {
                    this.$set(this.items, i, this.rowBeforeEdit)
                    break;
                }
            }
            
            this.editedRowId = null;
            this.showWriteoffDangerId = null;
            this.removeUnusedFields();
        },
        handleHidePaidItems() {
            this.hidePaidItems = !this.hidePaidItems;

            if (this.hidePaidItems) {
                this.items.forEach(item => {
                    let remainingValue = this.getRemaining(item);
                    if (remainingValue === 0)
                        item.hided = true;
                });
            } else {
                this.items.forEach(item => {
                    item.hided = false;
                });
            }
            
            // makes the filtering reactive
            let numberKey = parseInt(this.filterKey);
            numberKey++;
            this.filterKey = numberKey.toString();
        },
        isInViewport(element_id) {
            var element = window.document.getElementById(element_id);
            var doc = window.document.getElementById('table-container');
            if (element && doc) {
                var rect = element.getBoundingClientRect();
                var docRect = doc.getBoundingClientRect();

                return (
                    rect.top >= 0 &&
                    rect.left >= 0 &&
                    rect.bottom <= docRect.bottom &&
                    rect.right <= docRect.right
                );
            }
        },
        handleScroll(){
            if (!this.fetchingItems && this.nextUrl) {
                
                var loadNewItems = this.isInViewport('load_more_button');
                if (loadNewItems) {
                    this.fetchingItems = true;

                    BalanceSheetService.loadPage(this.nextUrl).then((response) => {
                        this.fetchingItems = false;
                        this.nextUrl = response.data.next;
                        this.items = this.items.concat(response.data.results);

                        this.items.forEach(item => {
                            item.writeoffyears_count = item.writeoffyear_set.length;

                            item.writeoffyear_set.forEach(writeoffyear => {
                                this.insertYearField(writeoffyear.year.toString());

                                item[writeoffyear.year.toString()] = writeoffyear.amount;
                            });
                        });

                        this.removeUnusedFields();
                        this.loading = false;
                    });
                }
            }
        },
        writeoffBorderClass(row) {
            if (this.showWriteoffDangerId === row.id)
                return "danger-row-class";
        },
        isYearInRange(data) {
            let date = Date.parse(data.item.date);
            let itemStartYear = date.getFullYear();
            let itemMaxYear = itemStartYear + this.maxWriteoffYears
            let itemEndYear = itemStartYear + data.item.writeoffyears_count;
            let today = new Date();
            let currentYear = today.getFullYear();
            let fieldYear = parseInt(data.field.key);
            
            return ((fieldYear >= itemStartYear && fieldYear <= itemEndYear) 
            || (fieldYear >= itemStartYear && fieldYear <= currentYear) 
            || (fieldYear === currentYear && itemStartYear <= currentYear)) && (fieldYear < itemMaxYear);
        },
        isYearDisabled(data) {
            let date = Date.parse(data.item.date)
            let itemStartYear = date.getFullYear()
            let itemMaxYear = itemStartYear + this.maxWriteoffYears
            let fieldYear = parseInt(data.field.key)
            return fieldYear < itemStartYear || fieldYear >= itemMaxYear
        },
        isBalanceSheet(item) {
            let costType = this.costTypeOptions.find(option => option.value === item.cost_type);
            if (costType) {
                return costType.is_balance_sheet;
            }
            return true; // investment item with no cost attached
        },
        inputFormatter(value) {
            return parseFloat(value).toFixed(2).toString();
        },
        refreshData () {
            this.$refs['balancesheet-table-filterbar'].filtersChanged()
        },
    }
}
</script>

<style lang="scss">
.balance-sheet-table {
    // margin-bottom: 0 !important;
    td, th {
        border-top: none !important;
        vertical-align: middle !important;
    }
    thead {
        cursor: default;
    }
}

.spinner {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 50px;
}

.warning-text {
    div {
        display: inline;
    }
}

.danger-row-class {
    border: 2px solid #DE4752 !important;
}

@mixin table-sticky-items($side, $num, $colWidthList, $zIndex) {
  $selector: "";
  $background: "";
  @if $side == "left" {
    $selector: ":nth-child";
    $background: #fafafa;
  }
  @if $side == "right" {
    $selector: ":nth-last-child";
    $background: #fafafa;
  }
  @media( min-width: '900px' ){
    $position: 0px;
    @for $i from 1 through $num {
        &#{$selector}(#{$i}) {
            position: sticky;

            #{$side}: $position;
            z-index: $zIndex;
            background: $background;
            color: $colorTextBase;

            $position: $position + nth($colWidthList, $i);

            width: nth($colWidthList, $i);
            .fixed-table-content { width: inherit; }
        }
    }
  }
}

$columnWidth: 125px;
$columnWidthsLeft: 100px, 50px, 175px, 125px, 90px;
$columnWidthsRight: 125px, 140px; // from right to left
$fixedLeftCols: 5;
$fixedRightCols: 2;

.table-wrap {
//   max-height: 60vh;
//   overflow: auto;
  width: 100%;

//   margin-top: -10px;
}

table.balance-sheet-table {
    border: none;
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
    font-size: 16px;
    .fixed-table-content {
        padding: 0 10px;
        line-height: 1.2;
        margin-left: auto;
        justify-content: flex-end;
        white-space: nowrap;
        &.date-col {
            justify-content: flex-start;
            font-size: 14px;
        }
        &.costtype-col {
            color: $colorUnassigned;
            justify-content: center;
            .costtype-icon {

            }
        }
        &.description-col {
            justify-content: flex-start;
            div {
                overflow: hidden;
                text-overflow: ellipsis;
                text-align: left;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                font-size: 14px; 
                color: #9b9b9b;
            }
        }
        &.write-off-controls {
            justify-content: center;
            .controls-wrap {
                display: flex;
                align-items: center;
                .years-count {
                    line-height: 1;
                    font-size: 14px;
                    color: $colorTextBase;
                    width: 15px;
                }
            }
        }
        .write-off-controls {
            &--add, &--substract {
                color: $colorBlue;
                transition: color 0.2s ease;
                width: 25px;
                height: 25px;
                cursor: pointer;
                &:hover {
                    color: $colorBlue200;
                }
            }
        }
    }

  td, th {
    padding: 0px;
    text-align: center;
    > div {
        height: 48px;
        display: flex;
        align-items: center;
        width: 100%;
    }
  }

  thead {
    th {
      padding: 0px;
      position: sticky;
      top: 0;
      z-index: 1;
      width: $columnWidth;
      background: #ffffff !important;
      white-space: nowrap;
    //   text-transform: uppercase;
      font-size: 14px;
      color: $colorTextBase;
      border-bottom: 1px solid $tableBorderColor;
      @include table-sticky-items("left", $fixedLeftCols, $columnWidthsLeft, 2);
      @include table-sticky-items("right", $fixedRightCols, $columnWidthsRight, 2);
      > div {
        // height: auto;
        border: none;
      }
    }
  }
    thead th.fix-width .fixed-table-content { 
        display: flex; white-space: break-spaces;
        &.text-left {
            justify-content: flex-start;
        }
    }
  @media (min-width: '900px') {
    &.table-stretch { thead th:not(.fix-width) { width: auto } }
    &.table-fix { thead th:not(.fix-width) { width: $columnWidth } }
  }
  tbody {
    th {
      font-weight: 100;
      font-style: italic;
      text-align: left;
      position: relative;
      position: sticky;
      left: 0;
      background: #ffffff;
      z-index: 1;
    }
    tr {
        &:nth-child(odd) {
            background: white;
        }
        &:nth-child(even) {
            background: white;
        }
      td {
        padding: 0px 0 !important;

        outline: none;
        border-bottom: 1px solid #eaeaea;
        @include table-sticky-items("left", $fixedLeftCols, $columnWidthsLeft, 1);
        @include table-sticky-items("right", $fixedRightCols, $columnWidthsRight, 1);
      }
    }
  }

  input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        /* display: none; <- Crashes Chrome on hover */
        -webkit-appearance: none;
        margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
    }

    input[type=number] {
        -moz-appearance:textfield; /* Firefox */
    }

}

.inner-border-right {
    .fixed-table-content {
        margin: 0 auto;
    }
}
.inner-border-left {
    width: 100%;
    font-size: 20px;
    white-space: nowrap !important;
}

.shadow-left {
    text-align: right;
    display: flex;
    align-items: center;
    height: 100%;
    border-left: 1px solid $tableBorderColor;
    box-shadow: -2px 0px 8px 0px rgb(0 0 0 / 10%);
    clip-path: inset(0px 0px 0px -8px);
}
.shadow-right {
    // display: flex;
    align-items: center;
    height: 100%;
    border-right: 1px solid $tableBorderColor;
    box-shadow: 2px 0px 8px 0px rgb(0 0 0 / 10%);
    clip-path: inset(0px -8px 0px 0px);
}

.writeoff-header {
    font-size: 20px;
    color: $colorTextBase;
}

.writeoff-value-container {
    position: relative;
    padding: 0 10px;
    width: 100%;
    border-right: 1px solid #eaeaea;
    div { margin-left: auto; line-height: 1.2; }
    color: #787474;
    &.disabled-writeoff {
        padding: 0;
        // background-image: linear-gradient(135deg, #e6e6e67d 10%, #ffffff 10%, #ffffff 50%, #e6e6e67d 50%, #e6e6e67d 60%, #ffffff 60%, #ffffff 100%);
        // background-size: 7px 7px;
        background: #fafafa;
        width: 100%;
        height: 48px;
        border: 0;
        .writeoff-empty {
            display: none;
        }
    }
    .writeoff-empty {
        color: $colorUnassigned;
        opacity: 0.3;
    }
}

.table-row-editing {
    background: #e8f7f3 !important;
}
</style>

<style lang="scss" scoped>
.form-input {
    text-align: right;
    // max-width: 90px;
    height: 26px;
    padding: 0 5px;
    margin: 0;
    margin-left: auto;
}

.fixed-table-content.actions {
    .action {
        font-size: 16px;
        width: 20px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-left: 5px;
        &--edit, &--save, &--undo {
            color: $colorBlue;
            &:hover {
                color: #096288;
            }
        }
        &--delete {
            color: $colorDanger;
            &:hover {
                color: #ad2a33;
            }
        }
        &--warning {
            margin: 0;
            color: $colorWarning;
            cursor: auto;
        }
        &.disabled {
            cursor: auto;
            color: $colorUnassigned !important;
            opacity: 0.5;
            pointer-events: none;
            &:hover {
                color: $colorUnassigned !important;
            }
        }
    }
}
</style>